import type { Me } from '@aurea/contracts';
import { useTheme } from '@aurea/ui';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { BookOpen, CalendarDays, Link2, LogOut, Moon, Settings, Sun, Ticket, Users } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../app.css';
import { ImpersonationBanner, SubscriptionBanner } from '../components/SubscriptionBanner';
import { ApiError, api } from '../lib/api';
import { setLanguage } from '../lib/i18n';

export const meQuery = { queryKey: ['me'], queryFn: api.me, staleTime: 60_000 } as const;

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401)
        throw redirect({ to: '/login', search: { next: location.pathname } });
      throw e;
    }
  },
  component: AppLayout,
});

export const useMe = () => useSuspenseQuery(meQuery).data;
export const isAdmin = (me: Me) => me.roles.includes('admin');
/** Школа может вносить изменения: подписка не закончилась (иначе режим «только чтение»). */
export const isWritable = (me: Me) => me.school.status !== 'expired';
/** Администратор может что-то менять прямо сейчас. */
export const canManage = (me: Me) => isAdmin(me) && isWritable(me);

type NavItem = {
  to: '/' | '/schedule' | '/people' | '/courses' | '/packages' | '/invites' | '/settings';
  key: string;
  color: string;
  icon: typeof Sun;
  roles: Me['roles'];
  /** На телефоне не в нижней панели, а значком вверху экрана. */
  topOnMobile?: boolean;
};
const NAV: NavItem[] = [
  { to: '/', key: 'today', color: 'lemon', icon: Sun, roles: ['admin', 'teacher', 'student'] },
  {
    to: '/schedule',
    key: 'schedule',
    color: 'sky',
    icon: CalendarDays,
    roles: ['admin', 'teacher', 'student'],
  },
  { to: '/people', key: 'people', color: 'mint', icon: Users, roles: ['admin', 'teacher'] },
  { to: '/courses', key: 'courses', color: 'lilac', icon: BookOpen, roles: ['admin', 'teacher'] },
  { to: '/packages', key: 'packages', color: 'apricot', icon: Ticket, roles: ['admin'] },
  { to: '/invites', key: 'invites', color: 'pink', icon: Link2, roles: ['admin'] },
  { to: '/settings', key: 'settings', color: 'sand', icon: Settings, roles: ['admin'], topOnMobile: true },
];

function AppLayout() {
  const me = useMe();
  const { t, i18n } = useTranslation();
  const { isDark, toggle } = useTheme();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      qc.clear();
      navigate({ to: '/login' });
    },
  });

  // Язык интерфейса по умолчанию — из профиля/школы, пока пользователь не выбрал сам.
  useEffect(() => {
    try {
      if (!localStorage.getItem('aurea-lang') && i18n.language !== me.locale) i18n.changeLanguage(me.locale);
    } catch {}
  }, [me.locale, i18n]);

  const items = NAV.filter((n) => n.roles.some((r) => me.roles.includes(r)));
  const role = me.roles.includes('admin') ? 'admin' : me.roles.includes('teacher') ? 'teacher' : 'student';

  const tools = (
    <>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={toggle}
        aria-label={isDark ? t('common.themeLight') : t('common.themeDark')}
      >
        {isDark ? <Sun className="i" /> : <Moon className="i" />}
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => setLanguage(i18n.language === 'ru' ? 'en' : 'ru')}
      >
        {i18n.language === 'ru' ? 'EN' : 'RU'}
      </button>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => logout.mutate()}
        aria-label={t('common.logout')}
      >
        <LogOut className="i" />
      </button>
    </>
  );

  return (
    <div className="app">
      <nav className="rail" aria-label={t('nav.label')}>
        <div className="school-badge">
          {me.school.logoUrl ? (
            <img className="logo" src={me.school.logoUrl} alt="" />
          ) : (
            <div className="logo paper sticker" data-c={me.school.accentColor ?? 'lilac'} aria-hidden="true">
              {me.school.name.charAt(0)}
            </div>
          )}
          <div className="min-w-0">
            <b className="block truncate leading-tight">{me.school.name}</b>
            <span className="block truncate text-xs text-ink-3">{location.host}</span>
          </div>
        </div>
        <ul className="flags">
          {items.map(({ to, key, color, icon: Icon, topOnMobile }) => (
            <li key={to} className={topOnMobile ? 'hide-mobile' : undefined}>
              <Link
                to={to}
                className="flag"
                activeProps={{ className: 'on', 'aria-current': 'page' }}
                activeOptions={{ exact: to === '/' }}
                style={{ ['--c' as string]: `var(--p-${color})` }}
              >
                <Icon className="i" aria-hidden="true" />
                {t(`nav.${key}`)}
                {key === 'people' && <span className="count hide-mobile">{me.school.students}</span>}
              </Link>
            </li>
          ))}
        </ul>
        <div className="rail-foot">
          <div className="flex items-center gap-3 text-sm">
            <span className="avatar">{me.firstName.charAt(0) + me.lastName.charAt(0)}</span>
            <div className="min-w-0">
              <div className="truncate">
                {me.firstName} {me.lastName}
              </div>
              <div className="text-xs text-ink-3">{t(`roles.${role}`)}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">{tools}</div>
        </div>
      </nav>
      <main className="main">
        {/* На телефоне боковой панели нет — профиль, тема и выход вверху экрана. */}
        <div className="mobile-tools">
          <span className="mr-auto truncate text-sm font-semibold">{me.school.name}</span>
          {isAdmin(me) && (
            <Link to="/settings" className="btn btn-ghost" aria-label={t('nav.settings')}>
              <Settings className="i" />
            </Link>
          )}
          {tools}
        </div>
        <ImpersonationBanner />
        <SubscriptionBanner />
        <Outlet />
      </main>
    </div>
  );
}
